/**
 * ModalGrupos - Formulario de edición/creación de grupos
 *
 * Props:
 * - open: boolean que indica si el modal está abierto
 * - grupo: objeto con datos del grupo (null para crear nuevo)
 * - saving: boolean que indica si se está guardando
 * - onCancel: función para cancelar
 * - onSave: función para guardar (recibe el objeto actualizado)
 * - entrenadores: array de entrenadores disponibles para asignar
 */

import React, { useState, useEffect } from "react";
import Modal from "antd/es/modal/Modal";

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder,
  disabled = false,
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
        required={required}
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  required = false,
  placeholder,
  disabled = false,
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
        required={required}
      >
        <option value="">{placeholder || "Seleccionar..."}</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
      </select>
    </label>
  );
}

function HorarioDia({ dia, value, onChange, disabled, onCopyToAll, isDomingoExpanded, setDomingoExpanded, onError }) {
  // Estado para controlar si el día está expandido (domingo colapsado por defecto)
  const [isExpanded, setIsExpanded] = React.useState(dia !== "domingo");
  
  // Si es domingo, usar el estado compartido
  const expanded = dia === "domingo" ? isDomingoExpanded : isExpanded;
  const setExpanded = dia === "domingo" ? setDomingoExpanded : setIsExpanded;
  
  // Estados locales para los inputs individuales
  const [localInicio, setLocalInicio] = React.useState("");
  const [localFin, setLocalFin] = React.useState("");
  
  // Parsear el valor "7:30H - 9:30H" a formato HH:MM para input type="time"
  const parseHorario = (horario) => {
    if (!horario) return { inicio: "", fin: "" };
    const parts = horario.split(" - ");
    
    // Convertir "7:30H" a "07:30" (formato requerido por input type="time")
    const formatToTimeInput = (timeStr) => {
      if (!timeStr) return "";
      const cleaned = timeStr.replace("H", "").trim();
      const [hours, minutes] = cleaned.split(":");
      // Asegurar que las horas tengan 2 dígitos
      return `${hours.padStart(2, "0")}:${minutes || "00"}`;
    };
    
    return {
      inicio: formatToTimeInput(parts[0]) || "",
      fin: formatToTimeInput(parts[1]) || "",
    };
  };

  // Formatear de HH:MM a "7:30H - 9:30H"
  const formatHorario = (inicio, fin) => {
    if (!inicio || !fin) return "";
    
    // Convertir "07:30" a "7:30H" (removiendo el 0 inicial si existe)
    const formatFromTimeInput = (timeStr) => {
      const [hours, minutes] = timeStr.split(":");
      return `${parseInt(hours, 10)}:${minutes}H`;
    };
    
    return `${formatFromTimeInput(inicio)} - ${formatFromTimeInput(fin)}`;
  };

  // Sincronizar estados locales con el valor de props cuando cambia
  React.useEffect(() => {
    const { inicio, fin } = parseHorario(value);
    setLocalInicio(inicio);
    setLocalFin(fin);
  }, [value]);

  const inicio = localInicio;
  const fin = localFin;

  const handleInicioChange = (newInicio) => {
    setLocalInicio(newInicio);
    
    if (!newInicio) {
      // Si se borra la hora de inicio y no hay hora de fin, limpiar todo
      if (!localFin) {
        onChange("");
      }
      return;
    }
    
    // Validar que la hora de inicio no sea mayor que la hora de fin
    if (localFin && newInicio >= localFin) {
      if (onError) {
        const diaCapitalized = dia.charAt(0).toUpperCase() + dia.slice(1);
        onError(`${diaCapitalized}: La hora de inicio debe ser menor que la hora de fin`);
      }
      // Revertir el cambio
      setLocalInicio(inicio);
      return;
    }
    
    // Solo formatear y guardar si ambos valores están presentes
    if (localFin) {
      const formatted = formatHorario(newInicio, localFin);
      onChange(formatted);
    }
  };

  const handleFinChange = (newFin) => {
    setLocalFin(newFin);
    
    if (!newFin) {
      // Si se borra la hora de fin y no hay hora de inicio, limpiar todo
      if (!localInicio) {
        onChange("");
      }
      return;
    }
    
    // Validar que la hora de fin no sea menor que la hora de inicio
    if (localInicio && newFin <= localInicio) {
      if (onError) {
        const diaCapitalized = dia.charAt(0).toUpperCase() + dia.slice(1);
        onError(`${diaCapitalized}: La hora de fin debe ser mayor que la hora de inicio`);
      }
      // Revertir el cambio
      setLocalFin(fin);
      return;
    }
    
    // Solo formatear y guardar si ambos valores están presentes
    if (localInicio) {
      const formatted = formatHorario(localInicio, newFin);
      onChange(formatted);
    }
  };

  const handleClear = () => {
    onChange("");
  };

  const diaCapitalizado = dia.charAt(0).toUpperCase() + dia.slice(1);
  const hasValue = inicio && fin;
  
  // Si el domingo está colapsado, mostrar versión compacta
  if (dia === "domingo" && !expanded) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3">
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full flex items-center justify-between hover:text-blue-600 transition"
        >
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-700">
              {diaCapitalizado}
            </span>
            {hasValue && (
              <span className="text-[10px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
                {value}
              </span>
            )}
          </div>
          <svg 
            className="size-4 text-slate-400" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border ${hasValue ? 'border-blue-200 bg-blue-50/30' : 'border-slate-200 bg-slate-50/50'} p-3 space-y-2 transition-colors`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-700">
          {diaCapitalizado}
        </span>
        <div className="flex items-center gap-1">
          {hasValue && (
            <>
              <button
                type="button"
                onClick={handleClear}
                disabled={disabled}
                className="text-[10px] text-rose-600 hover:text-rose-700 hover:underline disabled:opacity-50 transition"
                title="Limpiar horario"
              >
                Limpiar
              </button>
              <span className="text-slate-300">•</span>
              <button
                type="button"
                onClick={() => onCopyToAll(value)}
                disabled={disabled}
                className="text-[10px] text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50 transition"
                title="Copiar este horario a todos los días"
              >
                Copiar a todos
              </button>
              {dia === "domingo" && <span className="text-slate-300">•</span>}
            </>
          )}
          {dia === "domingo" && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="text-slate-400 hover:text-slate-600 transition"
              title="Contraer"
            >
              <svg className="size-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block">
              <span className="block text-[10px] font-medium text-slate-600 mb-1">
                🕐 Hora inicio
              </span>
              <input
                type="time"
                value={inicio}
                onChange={(e) => handleInicioChange(e.target.value)}
                disabled={disabled}
                step="300"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
              />
            </label>
          </div>

          <div>
            <label className="block">
              <span className="block text-[10px] font-medium text-slate-600 mb-1">
                🕐 Hora fin
              </span>
              <input
                type="time"
                value={fin}
                onChange={(e) => handleFinChange(e.target.value)}
                disabled={disabled}
                step="300"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
              />
            </label>
          </div>
        </div>

        {hasValue && (
          <div className="flex items-center justify-center gap-1.5 text-xs font-medium text-blue-700 bg-white rounded-lg px-2 py-1.5 border border-blue-200 shadow-sm">
            <svg
              className="size-3.5 text-blue-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{value}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function ModalGrupos({
  open,
  editing,
  saving,
  onClose,
  onSubmit,
  entrenadores = [],
}) {
  const isEditing = !!editing?.id;
  const [loading, setLoading] = useState(true);
  const [isDomingoExpanded, setIsDomingoExpanded] = useState(false);
  const [edadError, setEdadError] = useState("");
  const [horarioError, setHorarioError] = useState("");

  const getInitialFormData = () => ({
    id: "",
    nombre: "",
    categoria: "",
    entrenador_principal_id: "",
    entrenador_secun_id: "",
    edad_minima: "",
    edad_maxima: "",
    descripcion: "",
    horarios: {
      lunes: "",
      martes: "",
      miercoles: "",
      jueves: "",
      viernes: "",
      sabado: "",
      domingo: "",
    },
  });

  const [formData, setFormData] = useState(getInitialFormData());

  // Cargar datos del grupo al montar el componente
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Simular pequeña carga para consistencia
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (editing) {
          setFormData({
            id: editing.id || "",
            nombre: editing.nombre || "",
            categoria: editing.categoria || "",
            entrenador_principal_id: editing.entrenador_principal_id || "",
            entrenador_secun_id: editing.entrenador_secun_id || editing.entrenador_secun || "",
            edad_minima: editing.edad_minima || "",
            edad_maxima: editing.edad_maxima || editing.edad_max || "",
            descripcion: editing.descripcion || "",
            horarios: {
              lunes: editing.horarios?.lunes || "",
              martes: editing.horarios?.martes || "",
              miercoles: editing.horarios?.miercoles || "",
              jueves: editing.horarios?.jueves || "",
              viernes: editing.horarios?.viernes || "",
              sabado: editing.horarios?.sabado || "",
              domingo: editing.horarios?.domingo || "",
            },
          });
        } else {
          // Limpiar formulario cuando se abre para crear
          setFormData(getInitialFormData());
        }
      } catch (error) {
        console.error("Error cargando datos del grupo:", error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      loadData();
    } else {
      // Limpiar formulario cuando se cierra el modal
      setFormData(getInitialFormData());
    }
  }, [editing, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Limpiar error previo
    setEdadError("");
    
    // Validar edades antes de enviar
    const edadMin = formData.edad_minima ? Number(formData.edad_minima) : null;
    const edadMax = formData.edad_maxima ? Number(formData.edad_maxima) : null;
    
    if (edadMin !== null && edadMax !== null && edadMin > edadMax) {
      setEdadError("La edad mínima no puede ser mayor que la edad máxima");
      setTimeout(() => setEdadError(""), 3000);
      return;
    }
    // Validar que entrenador principal y asistente no sean la misma persona
    if (
      formData.entrenador_principal_id &&
      formData.entrenador_secun_id &&
      formData.entrenador_principal_id === formData.entrenador_secun_id
    ) {
      setEdadError("El entrenador y el asistente no pueden ser la misma persona");
      setTimeout(() => setEdadError(""), 3000);
      return;
    }
    
    onSubmit(formData);
  };

  // Opciones para el select de entrenador - Filtrar solo entrenadores activos
  const entrenadorOptions = entrenadores
    .filter((e) => e.activo === true || e.activo === undefined) // Filtrar solo activos
    .map((e) => ({
      value: e.id,
      label: `${e.nombres} ${e.apellidos}`,
    }));

  // Opciones para el asistente: igual que entrenadorOptions pero marcamos como disabled la opción seleccionada en el otro select
  const asistenteOptions = entrenadorOptions.map((opt) => ({
    ...opt,
    disabled: opt.value === formData.entrenador_principal_id,
  }));
  const entrenadorOptionsWithDisabled = entrenadorOptions.map((opt) => ({
    ...opt,
    disabled: opt.value === formData.entrenador_secun_id,
  }));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={isEditing ? "Editar grupo" : "Nuevo grupo"}
      footer={null}
      width={800}
      centered
    >
      {loading ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <div className="h-3 w-20 bg-slate-200 rounded animate-pulse" />
                <div className="h-9 w-full bg-slate-100 rounded-xl animate-pulse" />
              </div>
            ))}
          </div>

          {/* Skeleton para horarios */}
          <div className="space-y-2">
            <div className="h-3 w-40 bg-slate-200 rounded animate-pulse" />
            <div className="h-2 w-64 bg-slate-100 rounded animate-pulse" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-slate-200 bg-slate-50/50 p-3 space-y-2"
                >
                  <div className="h-3 w-16 bg-slate-200 rounded animate-pulse" />
                  <div className="flex gap-2">
                    <div className="flex-1 h-16 bg-slate-100 rounded-lg animate-pulse" />
                    <div className="flex-1 h-16 bg-slate-100 rounded-lg animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <div className="h-3 w-24 bg-slate-200 rounded animate-pulse" />
            <div className="h-20 w-full bg-slate-100 rounded-xl animate-pulse" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <div className="h-9 w-20 bg-slate-100 rounded-xl animate-pulse" />
            <div className="h-9 w-32 bg-slate-200 rounded-xl animate-pulse" />
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label="Nombre del grupo"
              value={formData.nombre}
              onChange={(v) => setFormData((f) => ({ ...f, nombre: v }))}
              required
              disabled={saving}
              placeholder="Ej: Sub-12 Azul"
            />
            <SelectField
              label="Categoría"
              value={formData.categoria}
              onChange={(v) => setFormData((f) => ({ ...f, categoria: v }))}
              options={[
                { value: "Sub-6", label: "Sub-6" },
                { value: "Sub-7", label: "Sub-7" },
                { value: "Sub-8", label: "Sub-8" },
                { value: "Sub-9", label: "Sub-9" },
                { value: "Sub-10", label: "Sub-10" },
                { value: "Sub-11", label: "Sub-11" },
                { value: "Sub-12", label: "Sub-12" },
                { value: "Sub-13", label: "Sub-13" },
                { value: "Sub-14", label: "Sub-14" },
                { value: "Sub-15", label: "Sub-15" },
                { value: "Sub-16", label: "Sub-16" },
                { value: "Sub-17", label: "Sub-17" },
                { value: "Sub-18", label: "Sub-18" },
                { value: "Sub-19", label: "Sub-19" },
                { value: "Sub-20", label: "Sub-20" }
              ]}
              required
              disabled={saving}
              placeholder="Seleccionar categoría"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
            <div className="sm:col-span-6">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <SelectField
                    label="Entrenador"
                    value={formData.entrenador_principal_id}
                    onChange={(v) =>
                      setFormData((f) => ({ ...f, entrenador_principal_id: v }))
                    }
                    options={entrenadorOptionsWithDisabled}
                    required
                    disabled={saving}
                    placeholder="Selecciona un entrenador"
                  />
                </div>

                <div>
                  <SelectField
                    label="Asistente"
                    value={formData.entrenador_secun_id}
                    onChange={(v) =>
                      setFormData((f) => ({ ...f, entrenador_secun_id: v }))
                    }
                    options={asistenteOptions}
                    required={false}
                    disabled={saving}
                    placeholder="Selecciona un asistente"
                  />
                </div>
              </div>
            </div>
            <div className="sm:col-span-3">
              <Field
                label="Edad mínima"
                type="number"
                value={formData.edad_minima}
                onChange={(v) => setFormData((f) => ({ ...f, edad_minima: v }))}
                disabled={saving}
                placeholder="Ej: 10"
              />
            </div>
            <div className="sm:col-span-3">
              <Field
                label="Edad máxima"
                type="number"
                value={formData.edad_maxima}
                onChange={(v) => setFormData((f) => ({ ...f, edad_maxima: v }))}
                disabled={saving}
                placeholder="Ej: 12"
              />
            </div>
          </div>

          {/* Mensaje de error para edades */}
          {edadError && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 flex gap-2 animate-shake">
              <svg className="size-4 text-rose-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs text-rose-700 font-medium">
                {edadError}
              </span>
            </div>
          )}

          {/* Horarios */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <span className="block text-xs font-medium text-slate-700">
                  Horarios de entrenamiento
                </span>
                <span className="block text-[11px] text-slate-500 mt-0.5">
                  Define los horarios para cada día de la semana
                </span>
              </div>
            </div>

            {/* Mensaje de error para horarios */}
            {horarioError && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 flex gap-2 animate-pulse">
                <svg className="size-4 text-rose-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-xs text-rose-700 font-medium">
                  {horarioError}
                </span>
              </div>
            )}

            {/* Mensaje de ayuda */}
            <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 flex gap-2">
              <svg className="size-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-[11px] text-blue-700">
                <span className="font-medium">Tip:</span> Configura un horario y usa "Copiar a todos" para aplicarlo a todos los días de la semana.
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                "lunes",
                "martes",
                "miercoles",
                "jueves",
                "viernes",
                "sabado",
                "domingo",
              ].map((dia) => (
                <HorarioDia
                  key={dia}
                  dia={dia}
                  value={formData.horarios[dia]}
                  onChange={(newValue) =>
                    setFormData((f) => ({
                      ...f,
                      horarios: { ...f.horarios, [dia]: newValue },
                    }))
                  }
                  onCopyToAll={(horario) => {
                    // Si el domingo está contraído, no copiar el horario a domingo
                    setFormData((f) => ({
                      ...f,
                      horarios: {
                        lunes: horario,
                        martes: horario,
                        miercoles: horario,
                        jueves: horario,
                        viernes: horario,
                        sabado: horario,
                        // Solo copiar al domingo si está expandido
                        domingo: isDomingoExpanded ? horario : f.horarios.domingo,
                      },
                    }));
                  }}
                  onError={(message) => {
                    setHorarioError(message);
                    setTimeout(() => setHorarioError(""), 3000);
                  }}
                  disabled={saving}
                  isDomingoExpanded={isDomingoExpanded}
                  setDomingoExpanded={setIsDomingoExpanded}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Descripción
            </label>
            <textarea
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
              rows={3}
              value={formData.descripcion}
              onChange={(e) =>
                setFormData((f) => ({ ...f, descripcion: e.target.value }))
              }
              disabled={saving}
              placeholder="Información adicional sobre el grupo..."
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-blue-600 text-white px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-60 transition disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving && (
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {saving
                ? "Guardando..."
                : isEditing
                ? "Guardar cambios"
                : "Crear grupo"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
